import type { WhoisResult } from '../types/domain.types';
import { supabase } from '@/shared/lib/supabase';

/**
 * WHOIS / availability lookup service.
 *
 * Phase 8e.3: backed by the `domain-availability` Edge Function, which calls
 * domains.co.za /domain/check (always live — TTL=0 per AD-22).
 *
 * Limitations:
 * - The EF only returns availability ("registered" or "available"), not full
 *   WHOIS data. Rich WHOIS for already-registered third-party domains needs
 *   an RDAP proxy and is deferred to a later slice.
 * - The EF rejects TLDs not in `system_setting.domains_tld_allow_list`
 *   (AD-23) — currently `.co.za / .org.za / .net.za / .web.za`. The error
 *   bubbles up here as a clear message.
 */

const VALID_DOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;

const normaliseDomain = (raw: string): string =>
  raw.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');

interface AvailabilityResponse {
  available: boolean;
  fqdn: string;
  sld: string;
  tld: string;
  isPremium: boolean;
  usesEppKey: boolean;
  source: 'rdap' | 'zacr' | 'mock';
  lookedUpAt: string;
}

interface ApiErrorBody {
  code?: string;
  message?: string;
}

export const whoisService = {
  async lookup(query: string): Promise<WhoisResult> {
    const domain = normaliseDomain(query);
    if (!VALID_DOMAIN_RE.test(domain)) {
      throw new Error(
        'Enter a valid domain (e.g. example.co.za). Do not include "http://" or paths.',
      );
    }

    const { data, error } = await supabase.functions.invoke<AvailabilityResponse>(
      'domain-availability',
      { method: 'POST', body: { domain } },
    );

    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      if (body?.code === 'tld_not_supported') {
        throw new Error(body.message ?? 'TLD not yet supported.');
      }
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Lookup failed.');
    }

    if (!data) throw new Error('Lookup failed: empty response.');

    return {
      domain: data.fqdn,
      available: data.available,
      source: data.source,
      lookedUpAt: data.lookedUpAt,
    };
  },
};
